import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './auth.css';
import { API_BASE } from '../config/api';

const ConfirmReset = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
    }
  }, [token]);

  const validatePassword = (pwd) => {
    const minLength = pwd.length >= 8;
    const hasUppercase = /[A-Z]/.test(pwd);
    const hasLowercase = /[a-z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSymbol = /[!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?]/.test(pwd);
    return minLength && hasUppercase && hasLowercase && hasNumber && hasSymbol;
  };

  const getPasswordErrors = (pwd) => {
    const errors = [];
    if (pwd.length < 8) errors.push('at least 8 characters');
    if (!/[A-Z]/.test(pwd)) errors.push('one uppercase letter');
    if (!/[a-z]/.test(pwd)) errors.push('one lowercase letter');
    if (!/[0-9]/.test(pwd)) errors.push('one number');
    if (!/[!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?]/.test(pwd)) errors.push('one symbol');
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!token) {
      setError('Invalid reset token');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!validatePassword(newPassword)) {
      setError('Password must contain at least 8 characters, including uppercase, lowercase, number, and symbol');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/auth/reset-password/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword })
      });
      const data = await res.json().catch(() => ({}));
      
      if (!res.ok) {
        throw new Error(data.error || 'Failed to reset password');
      }

      setSuccess(data.message || 'Password reset successful! Redirecting to login...');
      setTimeout(() => {
        navigate('/auth/login');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again or request a new reset link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="auth-card">
        <h1>Set new password</h1>
        <p className="subtle">Enter your new password below.</p>
        <form onSubmit={handleSubmit}>
          <div className="auth-field">
            <label>New Password</label>
            <input 
              type="password" 
              value={newPassword} 
              onChange={(e) => setNewPassword(e.target.value)} 
              placeholder="Enter new password"
              required 
              disabled={loading || !token}
            />
            {newPassword && !validatePassword(newPassword) && (
              <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>
                Password must include: {getPasswordErrors(newPassword).join(', ')}
              </div>
            )}
          </div>
          <div className="auth-field">
            <label>Confirm Password</label>
            <input 
              type="password" 
              value={confirmPassword} 
              onChange={(e) => setConfirmPassword(e.target.value)} 
              placeholder="Confirm new password"
              required 
              disabled={loading || !token}
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>
                Passwords do not match
              </div>
            )}
          </div>
          {error && (
            <div className="auth-error" role="alert">{error}</div>
          )}
          {success && (
            <div className="auth-success" role="status">{success}</div>
          )}
          <div className="auth-actions">
            <button className="button" type="submit" disabled={loading || !token || success}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
            <a href="/auth/login">Back to login</a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConfirmReset;
